// A small vector flag (lib/flags.tsx), always paired with the country name in the caller's markup:
// a flag alone is never the only carrier. Unknown codes fall back to the ISO code in a neutral chip
// so a new wine country in druif degrades to text, never to a blank.
import "./config";
import { Text } from "tamagui";
import { localizedCountryName } from "@proef/domain";
import { flagShapes } from "../lib/flags";
import { useT } from "../lib/i18n";
import { Row } from "./layout";

export function CountryFlag({
  code,
  size = 16,
}: {
  code: string | undefined;
  size?: number | undefined;
}): React.JSX.Element | null {
  if (code === undefined || code === "") return null;
  const shapes = flagShapes(code);
  if (shapes === undefined) {
    return (
      <Text
        fontFamily="body"
        fontSize="0"
        fontWeight="700"
        letterSpacing={0.4}
        lineHeight="1"
        color="textDim"
        backgroundColor="surface2"
        paddingHorizontal={4}
        paddingVertical={1}
        borderRadius={2}
        flexShrink={0}
        aria-hidden
      >
        {code.toUpperCase()}
      </Text>
    );
  }
  return (
    <svg
      viewBox="0 0 24 16"
      width={size}
      height={Math.round((size * 2) / 3)}
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, borderRadius: 2, boxShadow: "0 0 0 1px var(--lineStrong)" }}
    >
      {shapes}
    </svg>
  );
}

/** The flag and the country's name in the reader's language. */
export function CountryLabel({
  code,
  size,
}: {
  code: string | undefined;
  size?: number;
}): React.JSX.Element | null {
  const { locale } = useT();
  if (code === undefined || code === "") return null;
  return (
    <Row render="span" display="inline-flex" gap="2">
      <CountryFlag code={code} size={size} />
      {localizedCountryName(code, locale)}
    </Row>
  );
}
